import type { ProductCombinationOptionGroup, ProductGalleryItem } from '~~/shared/types/store'
import type { VariantPicks } from '~~/app/utils/quoteVariant'

/**
 * Galeria wybranego koloru (BrandConfig.pdpGalleryFollowsColor).
 *
 * Backend przypina zdjęcie do wartości koloru (`images[].color_ids` →
 * `Product.galleryItems[].colorIds`); zdjęcie bez kolorów jest wspólne.
 *
 * - bez wybranego koloru → wszystkie zdjęcia w kolejności z backendu;
 * - wybrany kolor → NAJPIERW jego zdjęcia, POTEM wspólne (w obrębie każdej
 *   grupy kolejność z backendu). Zdjęcie przypięte do kilku kolorów jest przy
 *   każdym z nich. Wspólne na koniec, bo backend stawia je zwykle zaraz po
 *   okładce koloru głównego — w kolejności z backendu KAŻDY inny kolor
 *   zaczynał się od wspólnego zdjęcia (QA proles 288: po wyborze „żółty”
 *   dużym zdjęciem była tabela rozmiarów zamiast żółtej koszulki).
 *   Bez wspólnych zdjęć (dzisiejsze dane EasyItem) zostają same zdjęcia koloru;
 * - kolor bez ani jednego WŁASNEGO zdjęcia (także produkt bez żadnych
 *   przypisań) → wszystkie zdjęcia w kolejności z backendu, nie same wspólne:
 *   inaczej taki kolor zostałby z samymi wspólnymi albo bez zdjęć.
 */
export function variantGallery(
  items: readonly ProductGalleryItem[],
  selectedColorIds: readonly number[],
): string[] {
  const all = items.map(item => item.url)
  if (selectedColorIds.length === 0) return all

  const selected = new Set(selectedColorIds)
  const own = items.filter(item => item.colorIds.some(id => selected.has(id)))
  if (own.length === 0) return all

  const shared = items.filter(item => item.colorIds.length === 0)
  return [...own, ...shared].map(item => item.url)
}

/** Wybrane wartości grup kolorów (`isColor`) — wejście dla variantGallery. */
export function pickedColorIds(
  groups: readonly ProductCombinationOptionGroup[],
  picks: VariantPicks,
): number[] {
  return groups
    .filter(group => group.isColor)
    .map(group => picks[group.name])
    .filter((id): id is number => typeof id === 'number' && id > 0)
}
